import React from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentAbstractHitComponent from '@Components/Results/documents/document-abstract-hit.component';
import DocumentAbstractComponent from '@Components/Results/documents/document-abstract.component';
import DocumentClaimsComponent from '@Components/Results/documents/document-claims.component';
import DocumentClaimsHitComponent from '@Components/Results/documents/document-claims-hit.component';
import DocumentBibliographicInfoComponent from '@Components/Results/documents/document-bibliographic-info.component';
import DocumentIndexTermsHitComponent from '@Components/Results/documents/document-index-terms-hit.component';
import DocumentIndexTermsComponent from '@Components/Results/documents/document-index-terms.component';
import DocumentSubstancesComponent from '@Components/Results/documents/document-substances.component';
import DocumentReactionsComponent from '@Components/Results/documents/document-reactions.component';
import DocumentTargetsComponent from '@Components/Results/documents/document-targets.component';

function DocumentCollapsibleContentComponent({
  metadata,
  document: citation,
  zoom,
  toggleSection,
  abstractExpanded,
  claimsExpanded,
  indextermsExpanded,
  bibliographicInfoExpanded,
  substancesExpanded,
  hitSubstancesExpanded,
  reactionsExpanded,
  hitReactionsExpanded,
  targetsExpanded,
  getTrackingAttrs,
}) {
  const documentIndex = citation.index;
  const documentRelativeIndex = citation.relativeIndex;

  return (
    <>
      {/* Abstract */}
      {!!citation.abstractText && abstractExpanded && (
        <DocumentAbstractComponent abstractText={citation.abstractText} closeClick={(e) => toggleSection(e, 'abstractExpanded')} />
      )}

      {/* Hit Abstract */}
      {!!citation.abstractText && (
        <DocumentAbstractHitComponent
          abstractText={citation.abstractText}
          closeClick={(e) => toggleSection(e, 'abstractExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !abstractExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'abstract hit',
          })}
        />
      )}

      {/* Claims */}
      {!!citation.patentClaims && claimsExpanded && (
        <DocumentClaimsComponent claimsText={citation.patentClaims} closeClick={(e) => toggleSection(e, 'claimsExpanded')} />
      )}

      {/* Hit Claims */}
      {!!citation.patentClaims && (
        <DocumentClaimsHitComponent
          claimsText={citation.patentClaims}
          closeClick={(e) => toggleSection(e, 'claimsExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !claimsExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'claims hit',
          })}
        />
      )}

      {/* Index Terms */}
      {!!citation.keywords && indextermsExpanded && (
        <DocumentIndexTermsComponent keywords={citation.keywords} closeClick={(e) => toggleSection(e, 'indextermsExpanded')} />
      )}

      {/* Hit Index Terms */}
      {!!citation.keywords && (
        <DocumentIndexTermsHitComponent
          keywords={citation.keywords}
          closeClick={(e) => toggleSection(e, 'indextermsExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !indextermsExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'index terms hit',
          })}
        />
      )}

      {/* Substances */}
      {!!citation.substancesCount && substancesExpanded && (
        <DocumentSubstancesComponent
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesCount}
          isHit={false}
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'substancesExpanded')}
        />
      )}

      {/* Hit Substances */}
      {!!citation.substancesHitCount && hitSubstancesExpanded && (
        <DocumentSubstancesComponent
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesHitCount}
          isHit
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'hitSubstancesExpanded')}
        />
      )}

      {/* Reactions */}
      {!!citation.reactionsCount && reactionsExpanded && (
        <DocumentReactionsComponent
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsCount}
          isHit={false}
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'reactionsExpanded')}
        />
      )}

      {/* Hit Reactions */}
      {!!citation.reactionsHitCount && hitReactionsExpanded && (
        <DocumentReactionsComponent
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsHitCount}
          isHit
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'hitReactionsExpanded')}
        />
      )}

      {/* Targets */}
      {!!citation.targetsCount && targetsExpanded && (
        <DocumentTargetsComponent
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          targetsCount={citation.targetsCount}
          closeClick={(e) => toggleSection(e, 'targetsExpanded')}
        />
      )}

      {/* Bibliographic Info */}
      {bibliographicInfoExpanded && (
        <DocumentBibliographicInfoComponent document={citation} closeClick={(e) => toggleSection(e, 'bibliographicInfoExpanded')} />
      )}
    </>
  );
}

DocumentCollapsibleContentComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  toggleSection: PropTypes.func.isRequired,
  abstractExpanded: PropTypes.bool.isRequired,
  claimsExpanded: PropTypes.bool.isRequired,
  indextermsExpanded: PropTypes.bool.isRequired,
  bibliographicInfoExpanded: PropTypes.bool.isRequired,
  substancesExpanded: PropTypes.bool.isRequired,
  hitSubstancesExpanded: PropTypes.bool.isRequired,
  reactionsExpanded: PropTypes.bool.isRequired,
  hitReactionsExpanded: PropTypes.bool.isRequired,
  targetsExpanded: PropTypes.bool.isRequired,
  // for analytics
  getTrackingAttrs: PropTypes.func.isRequired,
};

export default DocumentCollapsibleContentComponent;
